import { Box, Button, Card, Stack, Text } from "@bookingcom/bui-react";
import { UIFlightData } from "@flights/types";
import React from "react";
import FlightCardPrice from "./components/FlightCardPrice";
import { isOfMetaOrigin } from "../../../utils/marketing-url-params";
import ArrowNavUpIcon from "@bookingcom/bui-assets-react/streamline/ArrowNavUpIcon";
import ArrowNavDownIcon from "@bookingcom/bui-assets-react/streamline/ArrowNavDownIcon";
import { CardSegment } from "@flights/search-ui";
import { FlightCardLuggageDesktop } from "./components/elements/FlightCardLuggage/FlightCardLuggage.desktop";
import useFlightCard2Controller from "./useFlightCard2.controller";
import { FlightCardHighlight, FlightCardHighlightStyle } from "./components/FlightCardHighlight";
import FlightCardBadgesList from "./components/FlightCardBadgesList";
import { FareSelectorDesktop } from "../../../branded-fares/components/FareSelector/components/FareSelector.desktop";
import FlightCardCampaignBadgesList from "./components/FlightCardCampaignBadgesList";

type Props = {
  flight: UIFlightData;
  index: number;
  onClick: (flight: UIFlightData) => void;
};

const FlightCard2Desktop = ({ flight, index, onClick }: Props) => {
  const {
    breakdownOpen,
    setBreakdownOpen,
    travellersCount,
    canShowFareSelector,
    toggleShowFareSelector,
    showFareSelector,
    isResponsiveFixesSm,
    segmentsData,
    carrierText,
    highlightFireflySR,
    shouldPinOfferOnSr,
    bestOfferHighlightStyle,
    bestOfferHighlightText,
    isRTL,
    flightCardBoundRef,

    priceLabel,
    priceAriaLabel,
    fareOptionsCta,
    viewDetailsCta,
    flightPinnedLabel
  } = useFlightCard2Controller(flight);

  return (
    <Box attributes={{ id: `flight-card-${index}`, ref: flightCardBoundRef }} mixin={{ marginBlockEnd: 4 }} padding={0}>
      <Card fill={true} variant={shouldPinOfferOnSr ? "elevated" : highlightFireflySR ? "highlighted" : "neutral"}>
        {shouldPinOfferOnSr ? (
          <FlightCardHighlight style={FlightCardHighlightStyle.PRIMARY} text={flightPinnedLabel} />
        ) : bestOfferHighlightStyle && bestOfferHighlightText ? (
          <FlightCardHighlight style={bestOfferHighlightStyle} text={bestOfferHighlightText} textEmphasized={true} />
        ) : null}

        <Stack direction={{ s: "column", l: "row" }} divided={true} gap={0}>
          {/* LEFT SIDE */}
          <Stack gap={2} mixin={{ padding: 4, width: { s: "100%", l: "55%" } }}>
            <FlightCardBadgesList flight={flight} />

            <Stack gap={1}>
              {segmentsData.map((segmentData, index) => (
                <CardSegment
                  key={index}
                  {...segmentData}
                  stops={{ ...segmentData.stops }}
                  size="medium"
                  index={index}
                  isResponsiveFixesSm={isResponsiveFixesSm}
                  isForUpdatedFlightCard={true}
                />
              ))}
              <Text variant="small_1" attributes={{ "data-testid": "flight_card_carriers" }}>
                {carrierText}
              </Text>
            </Stack>
          </Stack>

          {/* RIGHT SIDE */}
          <Stack.Item grow={true} alignSelf="center" mixin={{ width: { s: "100%", l: "auto" }, padding: 4 }}>
            <Stack gap={2}>
              <FlightCardCampaignBadgesList flight={flight} />

              <Stack direction="row" gap={4} alignItems="center" justifyContent="space-between">
                <FlightCardLuggageDesktop
                  flight={flight}
                  brandedFareInfo={canShowFareSelector ? flight.brandedFareInfo : undefined}
                  brandedFareInfoAlignment="start"
                  buttonAlignment="start"
                  iconsAlignment="start"
                />

                <Stack.Item grow={true} attributes={{ style: { textAlign: isRTL ? "left" : "right" } }}>
                  <FlightCardPrice
                    flight={flight}
                    breakdownOpen={breakdownOpen}
                    setBreakdownOpen={setBreakdownOpen}
                    showPricePerPerson={travellersCount > 1}
                    showAvailableSeats={isOfMetaOrigin() && travellersCount <= 2}
                    totalPriceLabel={priceLabel}
                    popoverCloseAriaLabel={priceAriaLabel}
                    isRounded={true}
                    isFlightCardUpdate={true}
                  />
                </Stack.Item>
              </Stack>

              <Stack grow={true} direction="row" gap={2}>
                {canShowFareSelector && (
                  <Stack.Item grow={true}>
                    <Button
                      attributes={{
                        "aria-expanded": !!showFareSelector,
                        "aria-controls": `flights-fare-selector-${index}`,
                        "aria-describedby": `flight-card-${index}`
                      }}
                      icon={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon}
                      iconPosition="end"
                      variant="tertiary"
                      onClick={toggleShowFareSelector}
                      text={fareOptionsCta}
                      wide={true}
                    />
                  </Stack.Item>
                )}

                <Stack.Item grow={true}>
                  <Button
                    attributes={{
                      "data-testid": "flight_card_bound_select_flight",
                      "aria-describedby": `flight-card-${index}`
                    }}
                    text={viewDetailsCta}
                    onClick={() => {
                      onClick(flight);
                    }}
                    variant="secondary"
                    wide={true}
                  />
                </Stack.Item>
              </Stack>
            </Stack>
          </Stack.Item>
        </Stack>

        {canShowFareSelector && (
          <div id={`flights-fare-selector-${index}`}>
            {showFareSelector && <FareSelectorDesktop flight={flight} isFlightCardUpdate={true} />}
          </div>
        )}
      </Card>
    </Box>
  );
};

export default FlightCard2Desktop;
